import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { EmptyState } from "@/components/app/lists/empty-state";
import { FilterChip } from "@/components/app/lists/filter-chip";
import { PageHeader } from "@/components/app/shell/page-header";
import { DataTable } from "@/components/app/patterns/data-table";
import { DataTableToolbar } from "@/components/app/patterns/data-table-toolbar";
import { RowActions } from "@/components/app/patterns/row-actions";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { NewPacketDialog } from "./-components/new-packet-dialog";
import { PipelineSummary } from "./-components/pipeline-summary";
import { queueColumns, type QueueRow } from "./-components/queue-row";

export const Route = createFileRoute("/_authenticated/_care/admissions/")({
  component: AdmissionsHub,
});

const FILTERS: { id: string; label: string; match: (row: QueueRow) => boolean }[] = [
  { id: "all", label: "All", match: () => true },
  { id: "attention", label: "Needs attention", match: (row) => row.attention },
  { id: "prepare", label: "In preparation", match: (row) => row.stage === "prepare" },
  { id: "collect", label: "Awaiting signatures", match: (row) => row.stage === "collect" },
  { id: "complete", label: "Completed", match: (row) => row.stage === "complete" },
];
const STALE_SIGNING_DAYS = 7;

function AdmissionsHub() {
  const { facilityId } = useFacility();
  const navigate = useNavigate();
  const canManage = useCan("admission:manage");
  const queue = useQuery(orpc.care.admissions.overview.queryOptions({ input: { facilityId } }));
  const [active, setActive] = useState("all");
  const [creating, setCreating] = useState(false);
  const [now] = useState(() => new Date());

  const rows = queue.data?.rows ?? [];
  const loading = queue.isPending;
  const error = queue.isError;
  const byStage = (stage: string) => rows.filter((row) => row.stage === stage);
  const preparing = byStage("prepare");
  const collecting = byStage("collect");
  const countersigning = byStage("countersign");
  const completed = byStage("complete");
  const attention = rows.filter((row) => row.attention).length;
  const stale = collecting.filter(
    (row) =>
      row.packet.sentAt &&
      now.getTime() - new Date(row.packet.sentAt).getTime() > STALE_SIGNING_DAYS * 86_400_000,
  ).length;
  const started = preparing.filter((row) => row.progress.formsDataComplete > 0).length;
  const completedThisMonth = completed.filter((row) => {
    const date = new Date(row.packet.updatedAt);
    return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
  }).length;
  const filter = FILTERS.find((entry) => entry.id === active) ?? FILTERS[0];
  const visible = filter ? rows.filter(filter.match) : rows;

  const open = (row: QueueRow) =>
    row.subject.kind === "lead"
      ? void navigate({
          to: "/crm/leads/$leadId/admission-packets/$packetId",
          params: { leadId: row.subject.id, packetId: row.packet.id },
        })
      : void navigate({
          to: "/residents/$residentId/admission-packets/$packetId",
          params: { residentId: row.subject.id, packetId: row.packet.id },
        });
  // A packet starts from a chart: with one building chosen it can be started
  // here, across every building the way in is the resident list.
  const newPacket = () =>
    facilityId === "all" ? void navigate({ to: "/residents" }) : setCreating(true);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Admissions"
        count={queue.isSuccess ? rows.length : undefined}
        /* With no packets the table's empty state says so, and what belongs here. */
        meta={
          loading
            ? "Loading…"
            : error
              ? "Could not load the admissions queue"
              : rows.length === 0
                ? undefined
                : attention === 0
                  ? "Nothing needs you right now"
                  : `${attention} ${attention === 1 ? "packet needs" : "packets need"} you now`
        }
        actions={canManage ? <Button onClick={newPacket}>New packet</Button> : null}
      />

      {/* Four stages all reading 0 say nothing the empty table does not; the summary arrives with
          the first packet, as the chips do. */}
      {!loading && !error && rows.length === 0 ? null : (
        <PipelineSummary
          loading={loading}
          error={error}
          counts={{
            prepare: preparing.length,
            collect: collecting.length,
            countersign: countersigning.length,
            complete: completed.length,
          }}
          facts={[
            started === 0
              ? `None of the ${preparing.length} in preparation has started intake`
              : `${started} of ${preparing.length} in preparation have started intake`,
            stale > 0
              ? `${stale} waiting on signatures over ${STALE_SIGNING_DAYS} days`
              : `none waiting on signatures over ${STALE_SIGNING_DAYS} days`,
            `${completedThisMonth} completed this month`,
          ]}
        />
      )}

      {error ? (
        <Card className="gap-0 py-0">
          <ReadFailed
            className="p-6"
            message="Could not load the admissions queue."
            error={queue.error}
            onRetry={() => void queue.refetch()}
            retrying={queue.isFetching}
          />
        </Card>
      ) : (
        <DataTable
          data={visible}
          columns={queueColumns()}
          noun="packets"
          rowId={(row) => row.packet.id}
          pending={loading}
          onRowClick={open}
          renderToolbar={(table) => (
            <DataTableToolbar
              table={table}
              searchPlaceholder="Search packets…"
              chips={
                // Five chips all reading 0 filter nothing; they arrive with the first packet.
                loading || rows.length
                  ? FILTERS.map((entry) => (
                      <FilterChip
                        key={entry.id}
                        selected={entry.id === active}
                        count={rows.filter(entry.match).length}
                        onClick={() => setActive(entry.id)}
                      >
                        {entry.label}
                      </FilterChip>
                    ))
                  : null
              }
            />
          )}
          renderRowActions={(row) => (
            // The row opens the packet on a click; the menu must not.
            <span
              role="presentation"
              className="contents"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <RowActions
                label={`${row.subject.firstName} ${row.subject.lastName}`}
                primary={{ label: row.nextAction.label, onSelect: () => open(row) }}
                items={[]}
              />
            </span>
          )}
          empty={
            <EmptyState
              title={rows.length === 0 ? "No packets in progress" : "No packets match this filter"}
              description={
                rows.length === 0
                  ? "Every admission packet started for this facility appears here, how much of the intake is done, who still has to sign, and the one thing it is waiting on."
                  : "Switch back to All to see every packet, including the ones already completed."
              }
              action={
                rows.length === 0 ? undefined : (
                  <Button variant="outline" onClick={() => setActive("all")}>
                    Show all packets
                  </Button>
                )
              }
            />
          }
        />
      )}
      <p className="text-muted-foreground text-xs">
        A packet signed by every party checks off the &quot;Admission packet completed&quot; step on
        the resident&apos;s Move-in timeline.
      </p>

      {facilityId === "all" ? null : (
        <NewPacketDialog open={creating} onOpenChange={setCreating} facilityId={facilityId} />
      )}
    </div>
  );
}
